import { Component, DestroyRef, OnInit, ViewEncapsulation, ViewChild, inject } from "@angular/core"
import { takeUntilDestroyed } from "@angular/core/rxjs-interop"
import { take } from "rxjs"
import { CommonModule } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { MatButtonModule } from "@angular/material/button"
import { MatCardModule } from "@angular/material/card"
import { MatDatepickerModule } from "@angular/material/datepicker"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatIconModule } from "@angular/material/icon"
import { MatInputModule } from "@angular/material/input"
import { MatSelectModule } from "@angular/material/select"
import { MatRadioModule } from "@angular/material/radio"
import { MatSliderModule } from "@angular/material/slider"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { SnackbarService } from "../../services/snackbar.service"
import { MatTooltipModule } from "@angular/material/tooltip"
import { MatDialogModule } from "@angular/material/dialog"
import { provideNativeDateAdapter } from "@angular/material/core"
import { Router, ActivatedRoute, RouterModule } from "@angular/router"
import { StationStoreService } from "../../services/station-store.service"
import { StationService } from "../../services/station.service"
import { StationRatesService, StationRate } from "../../services/station-rates.service"
import { RecordingService } from "../../services/recording.service"
import { StationScheduleService, MaximumsResponse } from "../../services/station-schedule.service"
import { ProfileService, UserProfile } from "../../services/profile.service"
import { LockoutService } from "../../services/lockout.service"
import { OrderRuleService } from "../../services/order-rule.service"
import { PermissionService } from "../../services/permission.service"
import { Station } from "../../models/station.model"
import { UsCity, formatCityLabel, formatCoverageCitiesLabel } from "../../models/city.model"
import { Recording } from "../../models/recording.model"
import { ConfirmDialogService } from "../../services/confirm-dialog.service"
import { CoverageCitySelectComponent } from "../../shared/coverage-city-select/coverage-city-select.component"
import { StationSelectFieldComponent } from "../../shared/station-select-field/station-select-field.component"
import {
  PENDING_PROMO_CONTEXT_KEY,
  PendingPromoContext,
} from "../my-offers/my-offers.component"
import {
  earliestScheduleStartIso,
  formatCalendarDate,
  parseIsoCalendarDate,
} from "../../utils/business-date"
import { spotLengthCategoryForAudioSeconds, spotLengthForAudioSeconds } from "../../utils/spot-length"
import {
  formatSchedulingApiError,
  leadTimeMinimumFromApiError,
  validateScheduleDateRangeClient,
} from "../../utils/scheduling-date-validation"
import { NoScrollInputDirective } from "../../directives/no-scroll-input.directive"
import { PageHeaderComponent } from "../../shared/page-header/page-header.component"

@Component({
  selector: "app-campaign-builder",
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatSelectModule,
    MatCardModule,
    MatDatepickerModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatRadioModule,
    MatSliderModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDialogModule,
    CoverageCitySelectComponent,
    StationSelectFieldComponent,
    NoScrollInputDirective,
    PageHeaderComponent,
  ],
  templateUrl: "./campaign-builder.component.html",
  styleUrl: "./campaign-builder.component.css",
  providers: [
    provideNativeDateAdapter(),
  ],
  encapsulation: ViewEncapsulation.None,
})
export class CampaignBuilderComponent implements OnInit {
  mode: "create" | "edit" = "create"
  scheduleId: number | null = null
  userId: number | null = null

  /** Pre-selected promo (from My Offers → Use Now). Locks the station selection. */
  pendingPromo: PendingPromoContext | null = null

  // Step 1: Station & Rate Selection
  stations: Station[] = []
  selectedCoverageCities: UsCity[] = []
  selectedStationId: number | null = null
  stationRates: StationRate[] = []
  selectedStationRateId: number | null = null
  loadingStations = false
  stationsLoadError = ""
  loadingRates = false

  showCoverageMapDialog = false
  coverageMapLoading = false

  // Step 2: Recording Selection
  recordings: Recording[] = []
  selectedRecordingIds: number[] = []
  loadingRecordings = false

  // Step 3: Date Range
  startDate: Date | null = null
  endDate: Date | null = null
  minDate = new Date()
  calendarStartAt = new Date()
  lockedDates = new Set<string>()
  /** ISO date (YYYY-MM-DD) for earliest selectable day — from lockout API (business timezone). */
  businessTodayIso: string | null = null
  loadingLockedDates = false
  scheduleReadOnly = false
  /** Station order rule minimumStartDate (lead time), YYYY-MM-DD in the business timezone. */
  orderRuleMinimumIso: string | null = null
  loadingOrderRule = false

  /** Cached from GET /stations/{id}/owner-order-terms after station selection. */
  ownerOrderTerms: string | null = null
  ownerOrderTermsHasTerms = false
  loadingOwnerOrderTerms = false
  /** True after a successful terms response for the current station. */
  ownerOrderTermsLoaded = false
  private ownerOrderTermsStationId: number | null = null

  // Step 4: Scheduling Mode
  schedulingMode: "target_quantity" | "target_impressions" = "target_quantity"
  targetQuantity = 0
  targetImpressions = 0

  // Maximums
  maximums: MaximumsResponse["maximums"] | null = null
  loadingMaximums = false

  // Preview loading state
  loadingPreview = false

  // Submission
  submitting = false

  private readonly destroyRef = inject(DestroyRef)
  /** Bumped per maximums/preview request; a newer request (or a selection change) wins. */
  private maximumsRequestSeq = 0
  private previewRequestSeq = 0
  /** Bumped per rates request so a slow response for an old station/recording is dropped. */
  private ratesRequestSeq = 0

  constructor(
    private stationStore: StationStoreService,
    private stationService: StationService,
    public stationRatesSvc: StationRatesService,
    private recordingService: RecordingService,
    private scheduleService: StationScheduleService,
    private profileService: ProfileService,
    private lockoutService: LockoutService,
    private orderRuleService: OrderRuleService,
    private permissionService: PermissionService,
    private router: Router,
    private route: ActivatedRoute,
    private snackbar: SnackbarService,
    private confirmDialog: ConfirmDialogService,
  ) { }

  get isUserOrderFlow(): boolean {
    return this.permissionService.isAdvertiserUserOnly()
  }

  get selectedStation(): Station | undefined {
    return this.stations.find((s) => s.id === this.selectedStationId)
  }

  formatCoverageCityLabel(city: UsCity): string {
    return formatCityLabel(city)
  }

  formatStationCoverage(station: Station | undefined): string {
    if (station?.coverage_cities?.length) {
      return formatCoverageCitiesLabel(station.coverage_cities)
    }
    if (!station?.coverage_area) return "Not specified"
    if (station.coverage_state_code && !station.coverage_area.includes(",")) {
      return `${station.coverage_area}, ${station.coverage_state_code}`
    }
    return station.coverage_area
  }

  ngOnInit() {
    // Calendar opens near typical booking window (adjust if product changes)
    this.calendarStartAt = new Date(new Date().getFullYear(), new Date().getMonth(), 1)

    // Capture whether a promo context arrived with this navigation (only via My Offers → Use Now)
    const promoFromState = (history.state?.pendingPromo as PendingPromoContext | undefined) || null
    if (promoFromState && promoFromState.code) {
      this.pendingPromo = promoFromState
      // Persist so a preview round-trip survives
      try {
        localStorage.setItem(PENDING_PROMO_CONTEXT_KEY, JSON.stringify(promoFromState))
      } catch {
        /* ignore */
      }
    }

    const cachedUser = this.profileService.getCurrentUser()

    if (cachedUser && cachedUser.userid) {
      this.userId = cachedUser.userid
      if (this.isUserOrderFlow) {
        this.runAfterProfileReady()
      } else {
        this.loadStations(() => {
          this.runAfterProfileReady()
        })
      }
    } else {
      this.snackbar.error("User profile not loaded. Please log in again.", 3000)
      this.router.navigate(["/login"])
      return
    }

    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      if (params["scheduleId"]) {
        this.scheduleId = Number(params["scheduleId"])
        this.mode = "edit"
        this.loadScheduleForEdit()
      }
      const payment = params["payment"]
      if (payment === "cancel") {
        this.snackbar.info("Payment canceled. You can try again.", 3000)
      }
    })
  }

  private runAfterProfileReady(): void {
    const navigation = this.router.getCurrentNavigation()
    const isFromPreview =
      navigation?.previousNavigation?.finalUrl?.toString().includes("campaign-preview") ||
      history.state?.fromPreview
    const wasInPreview = localStorage.getItem("wasInPreview") === "true"
    const startFresh = history.state?.freshCampaign === true

    if (startFresh && this.mode !== "edit") {
      this.pendingPromo = null
      try {
        localStorage.removeItem(PENDING_PROMO_CONTEXT_KEY)
      } catch {
        /* ignore */
      }
      localStorage.removeItem("campaignBuilderState")
      localStorage.removeItem("campaignPreview")
      localStorage.removeItem("campaignRequest")
      localStorage.removeItem("campaignPreviewDetails")
      localStorage.removeItem("wasInPreview")
      this.clearCurrentState()
    } else if (isFromPreview || wasInPreview) {
      if (!this.pendingPromo) {
        this.pendingPromo = this.readPersistedPromoContext()
      }
      this.restoreState()
      localStorage.removeItem("wasInPreview")
    } else if (this.mode !== "edit") {
      if (!this.pendingPromo) {
        this.pendingPromo = null
        try {
          localStorage.removeItem(PENDING_PROMO_CONTEXT_KEY)
        } catch {
          /* ignore */
        }
      }
      localStorage.removeItem("campaignBuilderState")
      localStorage.removeItem("campaignPreview")
      localStorage.removeItem("campaignRequest")
      localStorage.removeItem("campaignPreviewDetails")
      this.clearCurrentState()
    }

    if (!this.isUserOrderFlow) {
      this.applyPendingPromoStation()
    } else if (this.pendingPromo) {
      this.applyPendingPromoStationForUser()
    }
  }

  restoreState() {
    const savedState = localStorage.getItem("campaignBuilderState")
    if (savedState) {
      try {
        const state = JSON.parse(savedState)
        this.selectedCoverageCities = state.selectedCoverageCities || []
        this.selectedStationId = state.selectedStationId
        this.selectedRecordingIds = state.selectedRecordingIds || []
        this.startDate = state.startDate ? this.parseStoredCalendarDate(state.startDate) : null
        this.endDate = state.endDate ? this.parseStoredCalendarDate(state.endDate) : null
        this.schedulingMode = state.schedulingMode
        this.targetQuantity = state.targetQuantity
        this.targetImpressions = state.targetImpressions
        this.maximums = state.maximums

        if (this.isUserOrderFlow && this.selectedCoverageCities.length > 0) {
          this.loadStationsByCities(
            this.selectedCoverageCities.map((city) => city.name),
            () => {
              if (this.selectedStationId) {
                this.loadingRecordings = true
                this.loadingRates = true
                this.loadRecordings(this.selectedStationId, () => {
                  this.loadRatesForRestoration(this.selectedStationId!)
                  this.loadLockedDates(this.selectedStationId!)
                  this.loadOrderRule(this.selectedStationId!)
                  this.loadOwnerOrderTerms(this.selectedStationId!)
                })
              }
            },
          )
        } else if (this.selectedStationId) {
          this.loadingRecordings = true
          this.loadingRates = true
          // Load recordings for restored station, then rates (rates need audiolength from recordings)
          this.loadRecordings(this.selectedStationId, () => {
            this.loadRatesForRestoration(this.selectedStationId!)
            this.loadLockedDates(this.selectedStationId!)
            this.loadOrderRule(this.selectedStationId!)
            this.loadOwnerOrderTerms(this.selectedStationId!)
          })
        }
      } catch (e) {
        console.error("Error restoring campaign builder state", e)
      }
    }
  }

  loadRatesForRestoration(stationId: number) {
    this.loadingRates = true;

    let audiolength: number | undefined;
    if (this.selectedRecordingIds && this.selectedRecordingIds.length > 0) {
      const recording = this.recordings.find(r => r.id === this.selectedRecordingIds[0]);
      if (recording) {
        audiolength = recording.audiolength;
      }
    }

    const requestSeq = ++this.ratesRequestSeq;
    this.stationRatesSvc.getStationRates(stationId, undefined, undefined, audiolength).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        if (requestSeq !== this.ratesRequestSeq || this.selectedStationId !== stationId) return;
        this.stationRates = res.rates || [];
        this.loadingRates = false;

        const savedState = localStorage.getItem("campaignBuilderState");
        if (savedState) {
          const state = JSON.parse(savedState);
          if (state.selectedStationRateId && this.stationRates.some(r => r.id === state.selectedStationRateId)) {
            this.selectedStationRateId = state.selectedStationRateId;
          }
        }
      },
      error: (err) => {
        if (requestSeq !== this.ratesRequestSeq) return;
        console.error("Error loading station rates during restoration:", err);
        this.loadingRates = false;
      }
    });
  }

  getRecordingName(id: number): string {
    const recording = this.recordings.find((r) => r.id === id)
    return recording ? recording.name : ""
  }

  loadStations(callback?: () => void) {
    this.loadingStations = true
    this.stationsLoadError = ""
    // One-shot: watchStations() is a long-lived BehaviorSubject whose initial [] (store not
    // loaded yet) and later background-refresh emissions each re-ran the callback, which
    // clears the builder state/preview and drops a pending promo. getStationsList() resolves
    // once with the cached list or the first API load.
    this.stationStore.getStationsList().pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (stations) => {
        this.stations = [...stations].sort((a, b) => a.call_sign.localeCompare(b.call_sign))
        this.loadingStations = false
        if (callback) callback()
      },
      error: (err) => {
        console.error("Error loading stations:", err)
        this.stationsLoadError = "Failed to load stations"
        this.snackbar.error("Failed to load stations", 3000)
        this.loadingStations = false
        if (callback) callback()
      },
    })
  }

  onCoverageCitiesChange(cities: UsCity[]): void {
    if (!this.isUserOrderFlow) return

    this.selectedCoverageCities = cities
    this.selectedStationId = null
    this.stationRates = []
    this.selectedStationRateId = null
    this.recordings = []
    this.selectedRecordingIds = []
    this.maximums = null
    this.stations = []
    this.stationsLoadError = ""

    if (cities.length === 0) {
      return
    }

    this.loadStationsByCities(cities.map((city) => city.name))
  }

  loadStationsByCities(cityNames: string[], callback?: () => void): void {
    if (!cityNames.length) {
      this.stations = []
      if (callback) callback()
      return
    }

    this.loadingStations = true
    this.stationsLoadError = ""
    this.stationService.getAllStationsByCities(cityNames).subscribe({
      next: (response) => {
        this.stations = (response.stations || []).sort((a, b) =>
          a.call_sign.localeCompare(b.call_sign),
        )
        this.loadingStations = false
        if (callback) callback()
      },
      error: (err) => {
        console.error("Error loading stations by city:", err)
        this.stationsLoadError = "Failed to load stations for the selected cities"
        this.snackbar.error("Failed to load stations for selected cities", 3000)
        this.loadingStations = false
        if (callback) callback()
      },
    })
  }

  openCoverageMapDialog(): void {
    if (!this.selectedStation) return
    this.showCoverageMapDialog = true
  }

  closeCoverageMapDialog(): void {
    this.showCoverageMapDialog = false
  }

  private applyPendingPromoStationForUser(): void {
    if (!this.pendingPromo || this.pendingPromo.stationId == null) return

    this.stationService.getStation(this.pendingPromo.stationId).subscribe({
      next: (station) => {
        const cities: UsCity[] =
          station.coverage_cities?.length
            ? [...station.coverage_cities]
            : station.coverage_area
              ? [
                  {
                    id: station.coverage_city_id ?? null,
                    name: station.coverage_area,
                    state: station.coverage_state ?? null,
                    stateCode: station.coverage_state_code ?? null,
                  },
                ]
              : []

        if (!cities.length) {
          this.snackbar.error(
            `The station for promo "${this.pendingPromo!.code}" has no coverage city configured.`,
            4000,
          )
          this.clearPendingPromo()
          return
        }
        this.selectedCoverageCities = cities
        this.loadStationsByCities(
          cities.map((city) => city.name),
          () => this.applyPendingPromoStation(),
        )
      },
      error: () => {
        this.snackbar.error(
          `The station for promo "${this.pendingPromo!.code}" is not available for you.`,
          4000,
        )
        this.clearPendingPromo()
      },
    })
  }

  onStationChange() {
    if (!this.selectedStationId) {
      this.stationRates = []
      this.selectedStationRateId = null
      this.recordings = []
      this.selectedRecordingIds = []
      this.maximums = null
      this.businessTodayIso = null
      this.orderRuleMinimumIso = null
      this.clearOwnerOrderTerms()
      return
    }

    // If a promo is pending, block changing away from its locked station
    if (
      this.pendingPromo &&
      this.pendingPromo.stationId != null &&
      this.selectedStationId !== this.pendingPromo.stationId
    ) {
      this.handlePromoStationConflict(this.selectedStationId)
      return
    }

    this.continueStationChange(this.selectedStationId)
  }

  private continueStationChange(stationId: number) {
    // Reset dependent state when station changes; only fetch recordings (rates fetched after recordings selected)
    this.recordings = []
    this.selectedRecordingIds = []
    this.stationRates = []
    this.selectedStationRateId = null
    this.maximums = null

    this.loadLockedDates(stationId)
    this.loadOrderRule(stationId)
    this.loadOwnerOrderTerms(stationId)

    this.loadingRecordings = true
    this.loadRecordings(stationId)
  }

  private clearOwnerOrderTerms(): void {
    this.ownerOrderTerms = null
    this.ownerOrderTermsHasTerms = false
    this.loadingOwnerOrderTerms = false
    this.ownerOrderTermsLoaded = false
    this.ownerOrderTermsStationId = null
  }

  /** Fetch owner order terms once when the station is known (not via Owner APIs). */
  private loadOwnerOrderTerms(stationId: number): void {
    this.loadingOwnerOrderTerms = true
    this.ownerOrderTermsStationId = stationId
    this.ownerOrderTerms = null
    this.ownerOrderTermsHasTerms = false
    this.ownerOrderTermsLoaded = false

    this.stationService.getOwnerOrderTerms(stationId).subscribe({
      next: (res) => {
        if (this.ownerOrderTermsStationId !== stationId) {
          return
        }
        this.ownerOrderTermsHasTerms = !!res.has_terms
        this.ownerOrderTerms = res.has_terms ? res.owner_order_terms : null
        this.ownerOrderTermsLoaded = true
        this.loadingOwnerOrderTerms = false
      },
      error: (err) => {
        if (this.ownerOrderTermsStationId !== stationId) {
          return
        }
        console.error("Failed to load owner order terms:", err)
        // Do not mark as loaded — preview will retry the dedicated API.
        this.ownerOrderTerms = null
        this.ownerOrderTermsHasTerms = false
        this.ownerOrderTermsLoaded = false
        this.loadingOwnerOrderTerms = false
      },
    })
  }

  /**
   * Warn the user when they change the station away from the promo's locked station.
   * Two choices: keep the promo (revert station) or drop the promo (keep new station).
   */
  private handlePromoStationConflict(attemptedStationId: number) {
    const promo = this.pendingPromo
    if (!promo) {
      this.continueStationChange(attemptedStationId)
      return
    }
    const lockedStationName = promo.stationName || "the original station"

    this.confirmDialog
      .open({
        title: "Change station and drop promo?",
        message:
          `Promo "${promo.code}" only works on ${lockedStationName}. ` +
          `If you switch stations, this promo will no longer apply to your order.`,
        confirmLabel: "Drop promo and continue",
        cancelLabel: "Keep promo",
        confirmIcon: "warning",
      })
      .subscribe((dropPromo) => {
        if (dropPromo) {
          this.clearPendingPromo()
          this.continueStationChange(attemptedStationId)
        } else {
          // Revert dropdown back to the promo's station
          this.selectedStationId = promo.stationId
          // Loads for the promo station still in flight while the dialog was open were
          // dropped by the stale-station guards; re-issue just those, keeping selections.
          if (promo.stationId != null) {
            this.reloadDroppedLoads(promo.stationId)
          }
          this.snackbar.info(
            `Kept promo "${promo.code}" — station reset to ${lockedStationName}.`,
            3000,
          )
          // No state reset needed; user is back where they were
        }
      })
  }

  /**
   * Re-run only the loads whose responses were dropped (their loading flag is still set) for
   * the station the user returned to. Unlike continueStationChange this keeps the selected
   * recordings, rate and dates.
   */
  private reloadDroppedLoads(stationId: number): void {
    const needRates = this.loadingRates
    if (this.loadingLockedDates) this.loadLockedDates(stationId)
    if (this.loadingOrderRule) this.loadOrderRule(stationId)
    const reloadRates = () => {
      if (needRates) this.reloadRatesKeepingSelection(stationId)
    }
    if (this.loadingRecordings) {
      this.loadRecordings(stationId, reloadRates)
    } else {
      reloadRates()
    }
  }

  private reloadRatesKeepingSelection(stationId: number): void {
    const keepRateId = this.selectedStationRateId
    const recording = this.recordings.find((r) => r.id === this.selectedRecordingIds[0])
    const requestSeq = ++this.ratesRequestSeq
    this.loadingRates = true
    this.stationRatesSvc
      .getStationRates(stationId, undefined, undefined, recording?.audiolength)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (requestSeq !== this.ratesRequestSeq || this.selectedStationId !== stationId) return
          this.stationRates = res.rates || []
          this.loadingRates = false
          if (keepRateId != null && this.stationRates.some((r) => r.id === keepRateId)) {
            this.selectedStationRateId = keepRateId
          }
        },
        error: (err) => {
          if (requestSeq !== this.ratesRequestSeq) return
          console.error("Error reloading station rates:", err)
          this.loadingRates = false
        },
      })
  }

  private readPersistedPromoContext(): PendingPromoContext | null {
    try {
      const raw = localStorage.getItem(PENDING_PROMO_CONTEXT_KEY)
      if (!raw) return null
      const parsed = JSON.parse(raw) as PendingPromoContext
      if (parsed && parsed.code) return parsed
      return null
    } catch {
      return null
    }
  }

  private applyPendingPromoStation() {
    if (!this.pendingPromo || this.pendingPromo.stationId == null) return
    const exists = this.stations.some((s) => s.id === this.pendingPromo!.stationId)
    if (!exists) {
      this.snackbar.error(
        `The station for promo "${this.pendingPromo.code}" is not available for you. ` +
          `The promo has been removed.`,
        4000,
      )
      this.clearPendingPromo()
      return
    }
    // Avoid wiping a station the user already restored from a different path
    if (this.selectedStationId !== this.pendingPromo.stationId) {
      this.selectedStationId = this.pendingPromo.stationId
      this.continueStationChange(this.selectedStationId)
    }
    this.snackbar.info(
      `Using promo "${this.pendingPromo.code}" — station locked to ${this.pendingPromo.stationName}.`,
      4000,
    )
  }

  clearPendingPromo() {
    this.pendingPromo = null
    try {
      localStorage.removeItem(PENDING_PROMO_CONTEXT_KEY)
    } catch {
      /* ignore */
    }
  }

  get calendarMinDate(): Date | null {
    return this.businessTodayIso ? this.minDate : null
  }

  get datesReadyForScheduling(): boolean {
    return !!this.businessTodayIso && !this.loadingLockedDates && !this.loadingOrderRule
  }

  /** Earliest selectable start (YYYY-MM-DD): day after businessToday or the station lead time, whichever is later. */
  get earliestStartIso(): string | null {
    return this.businessTodayIso
      ? earliestScheduleStartIso(this.businessTodayIso, this.orderRuleMinimumIso)
      : null
  }

  private parseStoredCalendarDate(value: string | Date): Date {
    if (typeof value === "string") {
      return parseIsoCalendarDate(value.slice(0, 10))
    }
    return parseIsoCalendarDate(formatCalendarDate(value))
  }

  private getSelectedRangeValidationError(): string | null {
    if (!this.startDate || !this.endDate) return null
    return validateScheduleDateRangeClient(
      this.businessTodayIso,
      this.lockedDates,
      this.formatDate(this.startDate),
      this.formatDate(this.endDate),
      this.orderRuleMinimumIso,
    )
  }

  private applyBusinessTodayFromLockouts(res: { businessToday?: string }): void {
    if (res.businessToday) {
      this.businessTodayIso = res.businessToday
      this.refreshMinDate()
    }
  }

  private refreshMinDate(): void {
    const earliest = this.earliestStartIso
    if (earliest) this.minDate = parseIsoCalendarDate(earliest)
  }

  private loadLockedDates(stationId: number) {
    this.loadingLockedDates = true
    // Previous station's lockouts must not gate this station's calendar while loading.
    this.lockedDates = new Set()
    this.businessTodayIso = null
    this.lockoutService.getLockedDates(stationId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        if (this.selectedStationId !== stationId) return
        this.lockedDates = new Set(res.lockedDates || [])
        this.applyBusinessTodayFromLockouts(res)
        this.loadingLockedDates = false
        this.clearCampaignDatesIfBelowMinimum()
      },
      error: () => {
        if (this.selectedStationId !== stationId) return
        this.lockedDates = new Set()
        this.businessTodayIso = null
        this.loadingLockedDates = false
        this.snackbar.error(
          "Could not load schedule lockouts for this station. Date selection is unavailable.",
          5000,
        )
      },
    })
  }

  /**
   * Station order rule (lead time). The backend rejects start dates before minimumStartDate,
   * so the calendar must not offer them. On failure we fall back to businessToday + 1.
   */
  private loadOrderRule(stationId: number) {
    this.loadingOrderRule = true
    this.orderRuleMinimumIso = null
    this.orderRuleService.getStationOrderRule(stationId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (rule) => {
        if (this.selectedStationId !== stationId) return
        this.orderRuleMinimumIso = rule?.minimumStartDate || null
        this.loadingOrderRule = false
        this.refreshMinDate()
        this.clearCampaignDatesIfBelowMinimum()
      },
      error: (err) => {
        if (this.selectedStationId !== stationId) return
        console.error("Failed to load station order rule:", err)
        this.orderRuleMinimumIso = null
        this.loadingOrderRule = false
        this.clearCampaignDatesIfBelowMinimum()
      },
    })
  }

  /**
   * The backend rejected the start date for the station's lead time (the rule may have changed
   * since it loaded): adopt the minimum it named so the calendar stops offering those days.
   * The snackbar already shows the backend's reason; this does not add a second message.
   */
  private applyLeadTimeRejection(body: unknown): void {
    const minimum = leadTimeMinimumFromApiError(body as { details?: string[] } | null)
    if (!minimum) return
    if (!this.orderRuleMinimumIso || minimum > this.orderRuleMinimumIso) {
      this.orderRuleMinimumIso = minimum
      this.refreshMinDate()
    }
    if (this.startDate && this.formatDate(this.startDate) < minimum) {
      this.startDate = null
      this.endDate = null
      this.maximums = null
    }
  }

  availableDateFilter = (d: Date | null): boolean => {
    if (!d) return false
    const iso = this.formatDate(d)
    if (this.lockedDates.has(iso)) return false
    const earliest = this.earliestStartIso
    if (!earliest) return false
    return iso >= earliest
  }

  isDateLocked(isoOrDate: string | Date | null | undefined): boolean {
    if (!isoOrDate) return false
    const iso = typeof isoOrDate === "string" ? isoOrDate.slice(0, 10) : this.formatDate(isoOrDate)
    return this.lockedDates.has(iso)
  }

  private clearCampaignDatesIfBelowMinimum() {
    if (!this.startDate) return
    // Wait for both lockouts and the order rule so the check uses the final minimum.
    if (this.loadingLockedDates || this.loadingOrderRule) return
    const startStr = this.formatDate(this.startDate)
    const earliest = this.earliestStartIso
    if (!earliest) return
    if (startStr < earliest || this.isDateLocked(startStr)) {
      this.startDate = null
      this.endDate = null
      this.maximums = null
      const msg = this.isDateLocked(startStr)
        ? "Campaign dates were reset because the start date is locked for this station."
        : `Campaign dates were reset because this station's earliest start date is ${earliest}.`
      this.snackbar.info(msg, 5000)
    } else if (this.endDate && this.rangeIncludesLockedDate(this.startDate, this.endDate)) {
      this.startDate = null
      this.endDate = null
      this.maximums = null
      this.snackbar.info("Campaign dates were reset because the range includes a locked schedule date.", 5000)
    }
  }

  private rangeIncludesLockedDate(start: Date, end: Date): boolean {
    const cur = new Date(start)
    cur.setHours(0, 0, 0, 0)
    const endD = new Date(end)
    endD.setHours(0, 0, 0, 0)
    while (cur <= endD) {
      if (this.isDateLocked(cur)) return true
      cur.setDate(cur.getDate() + 1)
    }
    return false
  }

  onRateChange() {
    this.maximums = null;
    this.checkAndCalculateMaximums();
  }

  loadRecordings(stationId: number, callback?: () => void) {
    if (!this.userId || !stationId) return
    this.loadingRecordings = true

    this.recordingService
      .listAll({
        status: 1,
        station_id: stationId,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          // Station changed while loading: these recordings belong to the old station.
          if (this.selectedStationId !== stationId) return
          this.recordings = res.records || []
          this.loadingRecordings = false
          if (callback) callback()
        },
        error: (err) => {
          if (this.selectedStationId !== stationId) return
          console.error("Error loading recordings:", err)
          this.snackbar.error("Failed to load recordings", 3000)
          this.loadingRecordings = false
          if (callback) callback()
        },
      })
  }

  onRecordingsChange() {
    this.maximums = null
    this.validateRecordingSelection()

    if (this.selectedRecordingIds.length === 0) {
      this.stationRates = []
      this.selectedStationRateId = null
      this.checkAndCalculateMaximums()
      return
    }

    this.checkAndCalculateMaximums()

    // Fetch station rates only after recordings selected; filter by audiolength
    const recording = this.recordings.find((r) => r.id === this.selectedRecordingIds[0])
    const audiolength = recording?.audiolength
    this.loadingRates = true
    this.stationRates = []
    this.selectedStationRateId = null

    const stationId = this.selectedStationId!
    const requestSeq = ++this.ratesRequestSeq
    this.stationRatesSvc.getStationRates(stationId, undefined, undefined, audiolength).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        // Superseded by a newer station/recording selection.
        if (requestSeq !== this.ratesRequestSeq || this.selectedStationId !== stationId) return
        this.stationRates = res.rates || []
        this.loadingRates = false
        const savedState = localStorage.getItem("campaignBuilderState")
        if (savedState) {
          const state = JSON.parse(savedState)
          if (state.selectedStationRateId && this.stationRates.some((r) => r.id === state.selectedStationRateId)) {
            this.selectedStationRateId = state.selectedStationRateId
          }
        }
        if (this.filteredStationRates.length === 0 && this.stationRates.length > 0) {
          this.snackbar.warning("No rate available for the selected recording length", 3000)
        }
      },
      error: (err) => {
        if (requestSeq !== this.ratesRequestSeq) return
        console.error("Error loading station rates:", err)
        this.snackbar.error("Failed to load station rates", 3000)
        this.loadingRates = false
      },
    })
  }

  private validateRecordingSelection() {
    if (this.selectedRecordingIds.length <= 1) return;

    const firstRecording = this.recordings.find(
      r => r.id === this.selectedRecordingIds[0]
    );
    if (!firstRecording || firstRecording.audiolength == null) return;

    const firstCategory = this.getSpotLengthCategory(firstRecording.audiolength);

    const validRecordingIds = this.selectedRecordingIds.filter(id => {
      const recording = this.recordings.find(r => r.id === id);
      if (!recording || recording.audiolength == null) return false;

      return (
        this.getSpotLengthCategory(recording.audiolength) === firstCategory
      );
    });

    if (validRecordingIds.length !== this.selectedRecordingIds.length) {
      this.selectedRecordingIds = validRecordingIds;
      this.snackbar.warning(
        "Only recordings within the same spot-length category can be selected together.",
        5000,
      )
    }
  }

  private getSpotLengthCategory(length: number): string {
    // Same thresholds as get_stationrates.derive_spot_length_from_audio; audiolength is fractional.
    return spotLengthCategoryForAudioSeconds(length);
  }

  public getSpotLengthCategoryPublic(length?: number): string {
    if (length == null) return 'other';
    return this.getSpotLengthCategory(length);
  }

  /**
   * Expected station rate spot_length (15, 30, 60) for the selected recording length.
   * Used to show only the matching rate: < 20s → 15s, < 35s → 30s, ≤ 65s → 60s
   * (mirrors get_stationrates.derive_spot_length_from_audio).
   */
  getExpectedSpotLengthForSelectedRecording(): 15 | 30 | 60 | null {
    if (this.selectedRecordingIds.length === 0) return null
    const recording = this.recordings.find((r) => r.id === this.selectedRecordingIds[0])
    return spotLengthForAudioSeconds(recording?.audiolength)
  }

  /**
   * Station rates filtered to only the spot length that matches the selected recording.
   * Do not show all rate options—only 15s, 30s, or 60s matching the recording length.
   */
  get filteredStationRates(): StationRate[] {
    const expected = this.getExpectedSpotLengthForSelectedRecording()
    if (expected == null) return []
    return this.stationRates.filter((r) => r.spot_length === expected)
  }

  public getRecordingById(id: number): Recording | undefined {
    return this.recordings.find(r => r.id === id);
  }

  onDateChange() {
    this.maximums = null;
    this.checkAndCalculateMaximums();
  }

  private lastEndDate: string | null = null;
  onRangeChange() {
    // Called when either start or end date changes via range picker
    const currentEndDateStr = this.endDate ? this.formatDate(this.endDate) : null;
    if (this.startDate && this.endDate && currentEndDateStr !== this.lastEndDate) {
      this.lastEndDate = currentEndDateStr;
      this.maximums = null;
      this.checkAndCalculateMaximums();
    }
  }

  get impressionStep(): number {
    if (!this.maximums || !this.maximums.max_impressions) return 100;
    const max = this.maximums.max_impressions;
    if (max >= 100000) return 10000;
    if (max >= 10000) return 1000;
    if (max >= 1000) return 100;
    return 10;
  }

  get estimatedCost(): number | null {
    // Return null if we don't have the required data
    if (!this.selectedStationRateId || !this.maximums) {
      return null;
    }

    // Find the selected rate to get CPM and ratecard values
    const selectedRate = this.stationRates.find(r => r.id === this.selectedStationRateId);
    if (!selectedRate) {
      return null;
    }

    // Calculate based on scheduling mode
    if (this.schedulingMode === 'target_quantity') {
      // Quantity mode: ratecard × targetQuantity
      if (this.targetQuantity <= 0) {
        return 0;
      }
      const cost = selectedRate.ratecard * this.targetQuantity;
      return cost;
    } else {
      // Impressions mode: (CPM × targetImpressions) / 1000
      if (this.targetImpressions <= 0) {
        return 0;
      }
      const cost = (selectedRate.cpm * this.targetImpressions) / 1000;
      return cost;
    }
  }

  checkAndCalculateMaximums() {
    if (this.canCalculateMaximums()) {
      this.calculateMaximums()
    }
  }

  canCalculateMaximums(): boolean {
    return !!(
      this.selectedStationRateId &&
      this.selectedRecordingIds.length > 0 &&
      this.startDate &&
      this.endDate &&
      this.datesReadyForScheduling &&
      !this.getSelectedRangeValidationError()
    )
  }

  calculateMaximums() {
    if (!this.canCalculateMaximums()) {
      const rangeErr = this.getSelectedRangeValidationError()
      if (rangeErr) {
        this.snackbar.warning(rangeErr, 4000)
        return
      }
      this.snackbar.warning("Please select station rate, recordings, and date range", 3000)
      return
    }

    this.loadingMaximums = true
    const request = {
      startdate: this.formatDate(this.startDate!),
      enddate: this.formatDate(this.endDate!),
      station_rate_id: this.selectedStationRateId!,
      recording_ids: this.selectedRecordingIds,
    }
    // Drop a response once the station, rate, recordings or dates it was computed for changed.
    const requestSeq = ++this.maximumsRequestSeq
    const stationId = this.selectedStationId
    const isCurrent = () =>
      requestSeq === this.maximumsRequestSeq &&
      this.selectedStationId === stationId &&
      this.selectedStationRateId === request.station_rate_id

    this.scheduleService.getMaximums(request).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        if (!isCurrent()) {
          if (requestSeq === this.maximumsRequestSeq) this.loadingMaximums = false
          return
        }
        this.maximums = res.maximums
        this.loadingMaximums = false
        // Update targets based on new maximums if they exceed them
        if (this.maximums) {
          if (this.targetQuantity > this.maximums.max_ads) {
            this.targetQuantity = this.maximums.max_ads;
          } else if (this.targetQuantity < 0) {
            this.targetQuantity = 0;
          }

          if (this.targetImpressions > this.maximums.max_impressions) {
            this.targetImpressions = this.maximums.max_impressions;
          } else if (this.targetImpressions < 1) {
            this.targetImpressions = 0; // default to 0
          }
          // Round to nearest step, but ensure it ends in 0 unless it's very small
          const step = this.impressionStep;
          if (step >= 10) {
            this.targetImpressions = Math.round(this.targetImpressions / step) * step;
          }
          // If rounding made it 0 and max is high, maybe pick step as default?
          // User said it can be 0 or 100 or 1000 etc. based on maximum.
          // Let's keep it at 0 if it was 0, or pick a sensible default if it's a new calculation.

          if (this.targetImpressions === 0 && this.maximums.max_impressions >= step) {
            // If it's 0, let's keep it 0 as requested "can be 0 or 100..."
          }

          // Ensure it doesn't exceed maximum after rounding
          if (this.targetImpressions > this.maximums.max_impressions) {
            if (step >= 10) {
              this.targetImpressions = Math.floor(this.maximums.max_impressions / step) * step;
              if (this.targetImpressions < step) this.targetImpressions = this.maximums.max_impressions;
            } else {
              this.targetImpressions = this.maximums.max_impressions;
            }
          }
        }
        // Use a slight delay for snackbar to ensure it shows the update message clearly
        this.snackbar.success("Maximums updated", 2000)
      },
      error: (err) => {
        if (!isCurrent()) {
          if (requestSeq === this.maximumsRequestSeq) this.loadingMaximums = false
          return
        }
        console.error("Error calculating maximums:", err)
        this.snackbar.error(
          formatSchedulingApiError(err.error, "Failed to calculate maximums"),
          5000,
        )
        this.loadingMaximums = false
        this.applyLeadTimeRejection(err?.error)
      },
    })
  }

  onTargetQuantityChange() {
    if (this.maximums && this.targetQuantity > this.maximums.max_ads) {
      this.targetQuantity = this.maximums.max_ads;
    }
    if (this.targetQuantity < 0) {
      this.targetQuantity = 0;
    }
  }

  onTargetImpressionsChange() {
    if (this.maximums && this.targetImpressions > this.maximums.max_impressions) {
      this.targetImpressions = this.maximums.max_impressions;
    }
    if (this.targetImpressions < 0) {
      this.targetImpressions = 0;
    }
    // Round to nearest step, but ensure it ends in 0 unless it's very small
    const step = this.impressionStep;
    if (step >= 10) {
      this.targetImpressions = Math.round(this.targetImpressions / step) * step;
    }

    // Ensure it doesn't exceed maximum after rounding
    if (this.maximums && this.targetImpressions > this.maximums.max_impressions) {
      if (step >= 10) {
        this.targetImpressions = Math.floor(this.maximums.max_impressions / step) * step;
        if (this.targetImpressions < 0) this.targetImpressions = 0;
      } else {
        this.targetImpressions = this.maximums.max_impressions;
      }
    }
  }

  canPreview(): boolean {
    const hasBasicInfo = !!(
      this.selectedStationRateId &&
      this.selectedRecordingIds.length > 0 &&
      this.startDate &&
      this.endDate &&
      this.datesReadyForScheduling &&
      !this.getSelectedRangeValidationError()
    )

    if (!hasBasicInfo) return false

    if (this.schedulingMode === "target_quantity") {
      return this.targetQuantity > 0
    } else {
      return this.targetImpressions >= 10 // Require at least 10 impressions
    }
  }

  previewCampaign() {
    if (!this.canPreview() || !this.userId) {
      const rangeErr = this.getSelectedRangeValidationError()
      if (rangeErr) {
        this.snackbar.warning(rangeErr, 4000)
        return
      }
      this.snackbar.warning("Please complete all required fields", 2500)
      return
    }

    this.loadingPreview = true

    const request = {
      userid: this.userId,
      station_rate_id: this.selectedStationRateId!,
      recording_ids: this.selectedRecordingIds,
      scheduling_mode: this.schedulingMode,
      startdate: this.formatDate(this.startDate!),
      enddate: this.formatDate(this.endDate!),
      target_quantity: this.schedulingMode === "target_quantity" ? this.targetQuantity : undefined,
      target_impressions: this.schedulingMode === "target_impressions" ? this.targetImpressions : undefined,
    }

    // Save full state for persistence and restoration
    const builderState = {
      selectedCoverageCities: this.selectedCoverageCities,
      selectedStationId: this.selectedStationId,
      selectedStationRateId: this.selectedStationRateId,
      selectedRecordingIds: this.selectedRecordingIds,
      startDate: this.startDate ? this.formatDate(this.startDate) : null,
      endDate: this.endDate ? this.formatDate(this.endDate) : null,
      schedulingMode: this.schedulingMode,
      targetQuantity: this.targetQuantity,
      targetImpressions: this.targetImpressions,
      maximums: this.maximums
    };
    localStorage.setItem("campaignBuilderState", JSON.stringify(builderState));
    localStorage.setItem("wasInPreview", "true");

    // Everything the preview page shows must describe the station/rate this request was for,
    // not whatever is selected when the response arrives.
    const requestSeq = ++this.previewRequestSeq
    const requestStationId = this.selectedStationId
    const selectedRate = this.stationRates.find(r => r.id === request.station_rate_id);
    const selectedStation = this.stations.find(s => s.id === requestStationId);
    const termsLoaded = this.ownerOrderTermsLoaded && this.ownerOrderTermsStationId === requestStationId
    const termsHas = this.ownerOrderTermsHasTerms
    const terms = this.ownerOrderTerms
    const isCurrent = () =>
      requestSeq === this.previewRequestSeq &&
      this.selectedStationId === requestStationId &&
      this.selectedStationRateId === request.station_rate_id

    // First page of scheduled ads (preview page fetches further pages via query limit/offset)
    this.scheduleService.preview(request, { limit: 10, offset: 0 }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        if (!isCurrent()) {
          if (requestSeq === this.previewRequestSeq) this.loadingPreview = false
          return
        }
        this.loadingPreview = false

        const previewDetails: Record<string, unknown> = {
          station_id: requestStationId,
          owner_id: selectedStation?.owner_id ?? null,
          station_name: selectedStation?.name,
          station_call_sign: selectedStation?.call_sign,
          spot_length: selectedRate?.spot_length,
          time_range: selectedRate ? this.formatTimeRange(selectedRate.start_hour, selectedRate.end_hour) : "",
          days: selectedRate?.days,
          date_range: `${request.startdate} to ${request.enddate}`,
        }
        if (termsLoaded) {
          previewDetails["has_order_terms"] = termsHas
          previewDetails["owner_order_terms"] = termsHas ? terms : null
        }

        localStorage.setItem("campaignPreview", JSON.stringify(res.preview))
        localStorage.setItem("campaignRequest", JSON.stringify(request))
        localStorage.setItem("campaignPreviewDetails", JSON.stringify(previewDetails))

        this.router.navigate(["/campaign-preview"], {
          state: {
            preview: res.preview,
            campaignRequest: request,
            previewDetails: previewDetails
          },
        })
      },
      error: (err) => {
        if (!isCurrent()) {
          if (requestSeq === this.previewRequestSeq) this.loadingPreview = false
          return
        }
        console.error("Error generating preview:", err)
        this.snackbar.error(
          formatSchedulingApiError(err.error, "Failed to generate preview"),
          5000,
        )
        this.loadingPreview = false
        this.applyLeadTimeRejection(err?.error)
      },
    })
  }

  loadScheduleForEdit() {
    this.snackbar.info("Edit mode - Load schedule details here", 3000)
  }

  cancel() {
    localStorage.removeItem("campaignBuilderState");
    localStorage.removeItem("campaignPreview");
    localStorage.removeItem("campaignRequest");
    localStorage.removeItem("campaignPreviewDetails");
    localStorage.removeItem("wasInPreview");
    localStorage.removeItem(PENDING_PROMO_CONTEXT_KEY);
    this.pendingPromo = null;
    this.router.navigate(["/orders"])
  }

  clearCurrentState() {
    this.selectedCoverageCities = []
    this.selectedStationId = null
    this.selectedStationRateId = null
    this.selectedRecordingIds = []
    this.recordings = []
    this.stationRates = []
    this.startDate = null
    this.endDate = null
    this.schedulingMode = "target_quantity"
    this.targetQuantity = 0
    this.targetImpressions = 0
    this.maximums = null
  }

  formatDate(date: Date): string {
    return formatCalendarDate(date)
  }

  formatTimeRange(startHour: number, endHour: number): string {
    const formatHour = (hour: number): string => {
      if (hour === 0) return "12a"
      if (hour < 12) return `${hour}a`
      if (hour === 12) return "12p"
      return `${hour - 12}p`
    }

    const displayEndHour = endHour + 1 === 24 ? 0 : endHour + 1
    return `${formatHour(startHour)}-${formatHour(displayEndHour)}`
  }


  formatNumber(num: number): string {
    return new Intl.NumberFormat("en-US").format(num)
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }
}
